import { LIMITS, type ClientIntent, type TableSnapshot } from '@tctp/protocol';
import { type FormEvent, useEffect, useState } from 'react';
import { Modal } from './Modal';

export function GameConfigModal({ snapshot, act, onClose }: { snapshot: TableSnapshot; act: (intent: ClientIntent) => Promise<void>; onClose: () => void }) {
  const live = snapshot.hand !== null && snapshot.hand.phase !== 'complete';
  const c = snapshot.pendingConfig ?? snapshot.config;   // what the next deal will use: settings saved during this hand, else the current ones
  const [form, setForm] = useState({ smallBlind: '', bigBlind: '', defaultStack: '', actionSec: '', discardSec: '', bankSec: '', bankHands: '' });
  const [rabbitHunt, setRabbitHunt] = useState(false);
  const [runItTwice, setRunItTwice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  useEffect(() => {
    setForm({
      smallBlind: String(c.smallBlind), bigBlind: String(c.bigBlind), defaultStack: String(c.defaultStack),
      actionSec: c.actionTimerMs === null ? '' : String(c.actionTimerMs / 1000), discardSec: String(c.discardTimerMs / 1000),
      bankSec: c.timeBankMs === 0 ? '' : String(c.timeBankMs / 1000), bankHands: String(c.timeBankHands),
    });
    setRabbitHunt(c.rabbitHunt);
    setRunItTwice(c.runItTwice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c]);

  function save(e: FormEvent): void {
    e.preventDefault();
    const values = [form.smallBlind, form.bigBlind, form.defaultStack, form.discardSec].map(Number);
    if (values.some(n => !Number.isInteger(n) || n < 1)) { setError('Blinds, the stack and the discard timer must be whole numbers of at least 1'); return; }
    const [smallBlind, bigBlind, defaultStack, discardSec] = values as [number, number, number, number];
    if (bigBlind < smallBlind) { setError('Big blind must be at least the small blind'); return; }
    const minSec = LIMITS.timerMinMs / 1000;
    const maxSec = LIMITS.timerMaxMs / 1000;
    const actionSec = form.actionSec.trim() === '' ? null : Number(form.actionSec);   // blank: no decision limit
    if (actionSec !== null && (!Number.isInteger(actionSec) || actionSec < minSec || actionSec > maxSec)) { setError(`Decision time must be between ${minSec} and ${maxSec} seconds, or blank for no limit`); return; }
    if (!Number.isInteger(discardSec) || discardSec < minSec || discardSec > maxSec) { setError(`The discard timer must be between ${minSec} and ${maxSec} seconds`); return; }
    const bankSec = form.bankSec.trim() === '' ? 0 : Number(form.bankSec);   // blank: no bank
    if (!Number.isInteger(bankSec) || bankSec < 0 || bankSec > LIMITS.timeBankMaxMs / 1000) { setError(`The time bank must be between 0 and ${LIMITS.timeBankMaxMs / 1000} seconds, or blank for none`); return; }
    const bankHands = Number(form.bankHands);
    if (!Number.isInteger(bankHands) || bankHands < 1 || bankHands > LIMITS.timeBankHandsMax) { setError(`Hands to refill the bank must be between 1 and ${LIMITS.timeBankHandsMax}`); return; }
    setError(null);
    void act({ type: 'setConfig', config: { smallBlind, bigBlind, defaultStack, actionTimerMs: actionSec === null ? null : actionSec * 1000, discardTimerMs: discardSec * 1000, timeBankMs: bankSec * 1000, timeBankHands: bankHands, runItTwice, rabbitHunt } });
  }
  const field = (label: string, key: keyof typeof form, min: number, max?: number) => (
    <label>{label}<input type="number" inputMode="numeric" min={min} max={max} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} /></label>
  );
  const others = snapshot.seats.filter((s): s is NonNullable<typeof s> => s !== null && s.seat !== snapshot.you.seat);
  return (
    <Modal title="Game Configurations" onClose={onClose}>
      <form onSubmit={save} noValidate>
        <fieldset className="config-grid">
          {field('Small blind', 'smallBlind', 1)}
          {field('Big blind', 'bigBlind', 1)}
          {field('Default stack', 'defaultStack', 1)}
          {field('Decision time (s, blank = no limit)', 'actionSec', LIMITS.timerMinMs / 1000, LIMITS.timerMaxMs / 1000)}
          {field('Discard timer (s)', 'discardSec', LIMITS.timerMinMs / 1000, LIMITS.timerMaxMs / 1000)}
          {field('Time bank (s, blank = none)', 'bankSec', 0, LIMITS.timeBankMaxMs / 1000)}
          {field('Hands to refill the bank', 'bankHands', 1, LIMITS.timeBankHandsMax)}
          <label className="check span2"><input type="checkbox" checked={runItTwice} onChange={e => setRunItTwice(e.target.checked)} />Run it twice: ask players when an all-in ends the betting with cards to come (twice only if everyone says yes within 8 s)</label>
          <label className="check span2"><input type="checkbox" checked={rabbitHunt} onChange={e => setRabbitHunt(e.target.checked)} />Rabbit hunting: after a hand ends early, a player may see the cards that would have come</label>
        </fieldset>
        {live && <p className="muted">A hand is in progress: settings you save now apply from the next hand.</p>}
        {snapshot.pendingConfig && <p className="muted" data-testid="pending-note">Saved for the next hand.</p>}
        {error && <p role="alert" className="error">{error}</p>}
        <div className="modal-actions"><button type="submit" className="btn green">SAVE SETTINGS</button></div>
      </form>
      <h3>Seats</h3>
      {others.length === 0 && <p className="muted">No other players.</p>}
      {others.map(s => (
        <div key={s.seat} className="seat-row">
          <span>{s.nickname} <span className="muted">(seat {s.seat + 1}, {s.stack})</span></span>
          <button type="button" className="btn grey small" onClick={() => void act({ type: 'kick', seat: s.seat })}>KICK</button>
          <button type="button" className="btn grey small" onClick={() => void act({ type: 'transferHost', seat: s.seat })}>MAKE HOST</button>
        </div>
      ))}
      <h3>Session</h3>
      {!confirmEnd
        ? <button type="button" className="btn red" onClick={() => setConfirmEnd(true)}>END SESSION</button>
        : <div className="modal-actions"><button type="button" className="btn red" onClick={() => { setConfirmEnd(false); void act({ type: 'endSession' }); }}>CONFIRM END</button><button type="button" className="btn grey" onClick={() => setConfirmEnd(false)}>KEEP PLAYING</button></div>}
    </Modal>
  );
}
